import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isPermissionRefused } from '@citibox-panel/api-client';

export interface UseDeliveryStates {
  options: string[];
  isPending: boolean;
  isError: boolean;
}

// The flags carry the state, not the list: `options` is empty while pending, on a failed read and
// on an empty catalog — three cases the caller has to tell apart to say so.
export function useDeliveryStates(
  fetchDeliveryStates: () => Promise<string[]>,
): UseDeliveryStates {
  // The key names NO building, unlike the carriers read beside it: which states EXIST is a
  // property of the domain, not of the building on screen, so one entry serves every tab. It also
  // means the read happens about once a working session — nothing invalidates it, because a
  // «Entrega» transitioning does not change which states there are.
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['delivery-states'],
    queryFn: () => fetchDeliveryStates(),
  });

  // Memoized because the consumer lists `options` as an effect dependency: a bare `data ?? []` is a
  // fresh array on every render while the read has no data, which turns that effect into a loop.
  const options = useMemo(() => data ?? [], [data]);

  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint an inline failure under the access screen.
  return { options, isPending, isError: isError && !isPermissionRefused(error) };
}
